import { isOnline } from '../../platform/network'
import { SliceApiError, type SliceApi } from '../types'
import { blobToBuffer, type QueueStore, type QueuedQuestion } from './store'

/**
 * Очередь вопросов Штаба в PWA (этап 4, шаг 2; решение владельца 7а).
 *
 * Каждый вопрос чата сначала записывается на устройство, потом уходит. Так
 * одним путём закрыты три случая: нет сети (вопрос ждёт и уходит сам), сбой
 * отправки (переживает закрытие экрана и приложения) и обычная отправка.
 *
 * Отправка:
 * - по одному, в порядке записи; голос - сначала распознавание, распознанный
 *   текст сохраняется, потом вопрос с тем же client_message_id. Повтор после
 *   любого сбоя идёт с ним же: сервер второй вопрос не создаст;
 * - сеть пропала (сбой связи, ответ потерялся) - остановка, продолжение по
 *   появлению сети, открытию приложения или чата, а пока сеть есть - через
 *   нарастающую паузу;
 * - лимит Штаба (429) или подписка (423) - остановка, все ждущие вопросы
 *   получают правило тарифа и больше сами не уходят: повтор не поможет;
 * - голос не распознан (пусто или сбой распознавания на сервере) - вопрос
 *   помечается «Не распознано», очередь идёт дальше;
 * - сервер не принимает вопрос (направление недоступно, три сбоя подряд) -
 *   «Не отправлено», очередь идёт дальше.
 *
 * Одна отправка за раз на всё устройство: Web Locks, а без них - аренда в
 * IndexedDB. Повтор безопасен и без этого, но голос распознавался бы дважды.
 */

export type ActivePhase = 'transcribing' | 'sending'

export type HubQueueEvent =
  /** Очередь изменилась: перечитать. */
  | { type: 'changed'; userId: string }
  /** Какой вопрос сейчас уходит (null - никакой). */
  | { type: 'active'; userId: string; id: string | null; phase: ActivePhase | null }
  /** Вопрос принят сервером: в ленте его место занимают вопрос и ответ сервера. */
  | {
      type: 'sent'
      userId: string
      id: string
      assistantId: string
      text: string
      userMessageId: string
      assistantMessageId: string
      at: string
    }
  /** Отправка сорвалась так, что человеку нужно решить (не распознано, лимит, отказ). */
  | { type: 'problem'; userId: string; id: string }

export interface LockManagerLike {
  request<T>(name: string, cb: (lock: unknown) => Promise<T>): Promise<T>
}

export interface BroadcastLike {
  postMessage(msg: unknown): void
  addEventListener(type: 'message', cb: (e: { data: unknown }) => void): void
  removeEventListener(type: 'message', cb: (e: { data: unknown }) => void): void
  close?(): void
}

export interface HubQueueDeps {
  store: QueueStore
  api: Pick<SliceApi, 'sendHubMessage' | 'transcribeVoice'>
  /** Кто вошёл: null - никто, очередь не отправляется. */
  owner: () => string | null
  /** Текущее направление: в него записывается новый вопрос. */
  assistant: () => string | null
  online?: () => boolean
  /** navigator.locks; null - нет Web Locks, работает аренда в хранилище. */
  locks?: LockManagerLike | null
  /** Канал между вкладками одного приложения. */
  channel?: BroadcastLike | null
  nowMs?: () => number
  newId?: () => string
  /** Пауза перед повтором после сбоя связи при «есть сеть», по номеру сбоя подряд. */
  backoffMs?: number[]
  setTimer?: (cb: () => void, ms: number) => unknown
  clearTimer?: (t: unknown) => void
}

export interface HubQueue {
  /** Вопросы текущего пользователя в текущем направлении, в порядке записи. */
  list(): Promise<QueuedQuestion[]>
  addText(text: string): Promise<QueuedQuestion>
  addVoice(audio: Blob, durationMs: number, contextLabel?: string): Promise<QueuedQuestion>
  /** Удалить до отправки. false - вопрос как раз уходит. */
  remove(id: string): Promise<boolean>
  /** Повторить сейчас: «Не отправлено», «Не распознано». */
  retry(id: string): Promise<void>
  /** Сколько вопросов пользователя ещё не принято сервером - во всех направлениях. */
  unsentCount(): Promise<number>
  /**
   * Удалить все неотправленные вопросы пользователя во всех направлениях
   * (выход из аккаунта с предупреждением). Возвращает, сколько удалено.
   */
  discardAll(): Promise<number>
  /** Отправить ждущие вопросы, если есть сеть. Одновременные вызовы склеиваются. */
  kick(): Promise<void>
  /** Что уходит сейчас (в любой вкладке). */
  active(): { id: string; phase: ActivePhase } | null
  owner(): string | null
  /** Текущее направление. */
  assistant(): string | null
  persistent(): boolean
  subscribe(cb: (e: HubQueueEvent) => void): () => void
  /**
   * Подключить к приложению: события других вкладок и повторы по таймеру.
   * Возвращает отключение (снимает таймер и подписку на вкладки). Пара
   * подходит для useEffect, в том числе двойного монтирования StrictMode.
   */
  attach(): () => void
}

const LOCK_NAME = 'procoach-hub-queue'
const LEASE_TTL_MS = 60_000
/** После стольких сбоев сервера подряд вопрос ждёт человека, а не крутится. */
const MAX_SERVER_FAILURES = 3
/** Ответ сервера «распознавание не удалось» (POST /transcribe, 500): это «не распознано», а не сбой связи. */
const TRANSCRIPTION_FAILED = 'transcription failed'
const DEFAULT_BACKOFF = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000]

type Outcome = 'next' | 'stop_offline'

/** Идентификатор вопроса от клиента: один на вопрос, повтор идёт с ним же. */
export function newClientMessageId(): string {
  const c = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (c?.getRandomValues) c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function withContext(text: string, contextLabel: string | null | undefined): string {
  const t = text.trim()
  return contextLabel ? `[${contextLabel}] ${t}` : t
}

export function createHubQueue(deps: HubQueueDeps): HubQueue {
  const { store, api } = deps
  // «Есть сеть» - как у всего приложения: браузер в сети и сервер отвечает
  // (platform/network). Сбой запроса сообщает в признак сети слой данных
  // (liveApi), очередь второй раз этого не делает.
  const online = deps.online ?? isOnline
  const nowMs = deps.nowMs ?? (() => Date.now())
  const newId = deps.newId ?? newClientMessageId
  const backoff = deps.backoffMs ?? DEFAULT_BACKOFF
  const setTimer = deps.setTimer ?? ((cb, ms) => setTimeout(cb, ms))
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>))
  const tabId = newClientMessageId()

  const listeners = new Set<(e: HubQueueEvent) => void>()
  let current: { id: string; phase: ActivePhase } | null = null
  let localActive: string | null = null
  let running: Promise<void> | null = null
  let again = false
  let offlineStreak = 0
  let timer: unknown = null
  let attached = 0

  const deliver = (e: HubQueueEvent) => {
    if (e.type === 'active') current = e.id && e.phase ? { id: e.id, phase: e.phase } : null
    listeners.forEach((cb) => cb(e))
  }
  const emit = (e: HubQueueEvent) => {
    deliver(e)
    try {
      deps.channel?.postMessage(e)
    } catch {
      // вкладка без канала - остальные узнают при следующем открытии
    }
  }
  const onMessage = (m: { data: unknown }) => {
    const e = m.data as HubQueueEvent | undefined
    if (!e || typeof e !== 'object' || typeof e.type !== 'string') return
    deliver(e)
  }

  const setActive = (userId: string, id: string | null, phase: ActivePhase | null) => {
    localActive = id
    emit({ type: 'active', userId, id, phase })
  }

  const schedule = (ms: number) => {
    if (attached === 0) return
    if (timer !== null) clearTimer(timer)
    timer = setTimer(() => {
      timer = null
      void kick()
    }, ms)
  }

  /** Отправка одного вопроса. Вопрос перечитывается перед каждым шагом: его могли удалить. */
  async function process(q: QueuedQuestion): Promise<Outcome> {
    const userId = q.userId
    let text = q.text

    if (q.kind === 'voice' && !text) {
      setActive(userId, q.id, 'transcribing')
      let recognized: string
      try {
        const audio = new Blob([q.audio ?? new ArrayBuffer(0)], { type: q.mime || 'audio/webm' })
        recognized = (await api.transcribeVoice(audio, q.assistantId)).trim()
      } catch (err) {
        const kind = err instanceof SliceApiError ? err.kind : 'network'
        if (kind === 'network') return failOffline(q)
        if (kind === 'limit') return failLimit(q, (err as SliceApiError).message)
        if (kind === 'not_found' || kind === 'conflict') return failHard(q)
        // Сервер недоступен (502 при перезапуске и т. п.), а не «не распознал»:
        // как сбой связи, но не дольше трёх раз подряд
        if (kind === 'server' && (err as SliceApiError).message !== TRANSCRIPTION_FAILED) {
          const failures = (q.serverFailures ?? 0) + 1
          if (failures < MAX_SERVER_FAILURES) {
            await store.patch(q.id, { serverFailures: failures, lastErrorAt: new Date(nowMs()).toISOString() })
            emit({ type: 'changed', userId })
            return 'stop_offline'
          }
        }
        // Распознавание не удалось: человек решит, очередь идёт дальше
        await store.patch(q.id, { state: 'unrecognized', serverFailures: 0, lastErrorAt: undefined })
        emit({ type: 'problem', userId, id: q.id })
        return 'next'
      }
      if (!recognized) {
        await store.patch(q.id, { state: 'unrecognized', lastErrorAt: undefined })
        emit({ type: 'problem', userId, id: q.id })
        return 'next'
      }
      text = withContext(recognized, q.contextLabel)
      // Распознанный текст сохраняем, запись больше не нужна: второй раз не распознаём
      const saved = await store.patch(q.id, { text, audio: undefined, serverFailures: 0, lastErrorAt: undefined })
      if (!saved) return 'next'
      emit({ type: 'changed', userId })
    }

    // Отправлять нечего (пустой текст) - не крутить его по кругу
    if (!text) return failHard(q)
    if (!(await store.get(q.id))) return 'next'
    setActive(userId, q.id, 'sending')
    try {
      const res = await api.sendHubMessage(text, q.id, q.assistantId)
      await store.remove(q.id)
      offlineStreak = 0
      emit({
        type: 'sent',
        userId,
        id: q.id,
        assistantId: q.assistantId,
        text,
        userMessageId: res.userMessageId,
        assistantMessageId: res.assistantMessageId,
        at: new Date(nowMs()).toISOString(),
      })
      return 'next'
    } catch (err) {
      const kind = err instanceof SliceApiError ? err.kind : 'network'
      if (kind === 'network') return failOffline(q)
      if (kind === 'limit') return failLimit(q, (err as SliceApiError).message)
      if (kind === 'server') {
        const failures = (q.serverFailures ?? 0) + 1
        if (failures < MAX_SERVER_FAILURES) {
          await store.patch(q.id, { serverFailures: failures, lastErrorAt: new Date(nowMs()).toISOString() })
          emit({ type: 'changed', userId })
          // Сервер мог лежать целиком: остальные не дёргаем, повтор позже
          return 'stop_offline'
        }
      }
      // Направление недоступно, конфликт, сервер раз за разом отказывает
      return failHard(q)
    }
  }

  async function failHard(q: QueuedQuestion): Promise<Outcome> {
    await store.patch(q.id, { state: 'failed', serverFailures: 0, lastErrorAt: new Date(nowMs()).toISOString() })
    emit({ type: 'problem', userId: q.userId, id: q.id })
    return 'next'
  }

  async function failOffline(q: QueuedQuestion): Promise<Outcome> {
    await store.patch(q.id, { lastErrorAt: new Date(nowMs()).toISOString() })
    emit({ type: 'changed', userId: q.userId })
    return 'stop_offline'
  }

  /**
   * Правило тарифа: всем ждущим вопросам этого направления, чтобы не слать их
   * впустую и не крутить повторы. Лимит и подписка - у клуба, а у другого
   * направления может быть другой клуб: его вопросы идут дальше.
   */
  async function failLimit(q: QueuedQuestion, reason: string): Promise<Outcome> {
    for (const w of await store.list(q.userId)) {
      if (w.state === 'waiting' && w.assistantId === q.assistantId) {
        await store.patch(w.id, { state: 'limit', reason, lastErrorAt: undefined, serverFailures: 0 })
      }
    }
    emit({ type: 'problem', userId: q.userId, id: q.id })
    emit({ type: 'changed', userId: q.userId })
    return 'next'
  }

  /** Один проход очереди под замком. */
  async function drain(): Promise<'done' | 'offline' | 'busy'> {
    const userId = deps.owner()
    if (!userId) return 'done'
    // Замок наш: если другая вкладка числилась отправляющей, она уже закончила (или закрылась)
    if (current && current.id !== localActive) deliver({ type: 'active', userId, id: null, phase: null })
    try {
      for (;;) {
        if (deps.owner() !== userId) return 'done'
        if (!online()) return 'offline'
        const next = (await store.list(userId)).find((q) => q.state === 'waiting')
        if (!next) return 'done'
        const outcome = await process(next)
        if (outcome === 'stop_offline') return 'offline'
      }
    } finally {
      if (localActive) setActive(userId, null, null)
    }
  }

  async function locked(): Promise<'done' | 'offline' | 'busy'> {
    if (deps.locks) return deps.locks.request(LOCK_NAME, () => drain())
    if (!(await store.tryLease(tabId, LEASE_TTL_MS, nowMs()))) return 'busy'
    // Долгое распознавание не должно отдать аренду другой вкладке посреди вопроса
    const renew = setInterval(() => void store.tryLease(tabId, LEASE_TTL_MS, nowMs()), LEASE_TTL_MS / 3)
    try {
      return await drain()
    } finally {
      clearInterval(renew)
      await store.releaseLease(tabId).catch(() => {})
    }
  }

  function kick(): Promise<void> {
    if (running) {
      again = true
      return running
    }
    running = (async () => {
      let result: 'done' | 'offline' | 'busy' = 'done'
      do {
        again = false
        try {
          result = await locked()
        } catch {
          // Хранилище недоступно: попробуем при следующем поводе
          result = 'done'
        }
      } while (again)
      if (result === 'offline' && online()) {
        // Сеть «есть», а сервер недоступен: событие online не придёт, повторяем сами
        schedule(backoff[Math.min(offlineStreak, backoff.length - 1)])
        offlineStreak += 1
      } else if (result === 'busy') {
        // Отправляет другая вкладка; если она закрылась, аренда истечёт
        schedule(LEASE_TTL_MS)
      } else if (result === 'done') {
        offlineStreak = 0
      }
    })().finally(() => {
      running = null
    })
    return running
  }

  const requireOwner = () => {
    const userId = deps.owner()
    const assistantId = deps.assistant()
    if (!userId || !assistantId) throw new SliceApiError('no user or direction', 'not_found')
    return { userId, assistantId }
  }

  return {
    async list() {
      const userId = deps.owner()
      const assistantId = deps.assistant()
      if (!userId || !assistantId) return []
      return (await store.list(userId)).filter((q) => q.assistantId === assistantId)
    },
    async addText(text) {
      const { userId, assistantId } = requireOwner()
      const q = await store.add({
        id: newId(),
        userId,
        assistantId,
        createdAt: new Date(nowMs()).toISOString(),
        kind: 'text',
        text: text.trim(),
      })
      emit({ type: 'changed', userId })
      return q
    },
    async addVoice(audio, durationMs, contextLabel) {
      const { userId, assistantId } = requireOwner()
      const q = await store.add({
        id: newId(),
        userId,
        assistantId,
        createdAt: new Date(nowMs()).toISOString(),
        kind: 'voice',
        audio: await blobToBuffer(audio),
        mime: audio.type || 'audio/webm',
        durationMs: Math.max(0, Math.round(durationMs)),
        ...(contextLabel ? { contextLabel } : {}),
      })
      emit({ type: 'changed', userId })
      return q
    },
    async remove(id) {
      if (localActive === id) return false
      const q = await store.get(id)
      const userId = deps.owner()
      if (!q || q.userId !== userId) return true
      await store.remove(id)
      emit({ type: 'changed', userId })
      return true
    },
    async retry(id) {
      const q = await store.get(id)
      const userId = deps.owner()
      if (!q || q.userId !== userId) return
      await store.patch(id, { state: 'waiting', reason: undefined, serverFailures: 0, lastErrorAt: undefined })
      offlineStreak = 0
      emit({ type: 'changed', userId })
      await kick()
    },
    async unsentCount() {
      const userId = deps.owner()
      return userId ? (await store.list(userId)).length : 0
    },
    async discardAll() {
      const userId = deps.owner()
      if (!userId) return 0
      // Уходящий сейчас тоже удаляется: выход уводит со страницы, и запрос
      // прервётся. Если сервер успел его принять, повторно он не уйдёт.
      const all = await store.list(userId)
      for (const q of all) await store.remove(q.id)
      if (all.length > 0) emit({ type: 'changed', userId })
      return all.length
    },
    kick,
    active: () => current,
    owner: () => deps.owner(),
    assistant: () => deps.assistant(),
    persistent: () => store.persistent(),
    subscribe(cb) {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    attach() {
      attached += 1
      if (attached === 1) deps.channel?.addEventListener('message', onMessage)
      let done = false
      return () => {
        if (done) return
        done = true
        attached -= 1
        if (attached > 0) return
        if (timer !== null) clearTimer(timer)
        timer = null
        deps.channel?.removeEventListener('message', onMessage)
      }
    },
  }
}
